/**
 * Steward Adapter Utilities
 * Helper functions for transforming and formatting steward data
 */

interface Steward {
  id: number | string;
  name: string;
  email?: string;
  stewardCode?: string;
  active: boolean;
  assignedCount: number;
  lastLogin?: string | null;
  createdAt?: string;
  activeRequests?: number;
  /** Backend flag from StewardResponse; true → finance-entry-only restriction. */
  financeOnlyRestricted?: boolean;
}

interface StewardStats {
  total: number;
  active: number;
  inactive: number;
  totalAssigned: number;
  totalActive: number;
}

/**
 * Transform raw API response to Steward type
 * Handles various API response formats and ensures consistent structure
 */
export function toSteward(rawData: any): Steward {
  return {
    id: rawData.id || rawData.stewardId || 0,
    name: rawData.name || rawData.stewardName || 'Unknown',
    email: rawData.email || undefined,
    stewardCode: rawData.stewardCode || rawData.code || undefined,
    active: rawData.active !== undefined ? rawData.active : rawData.isActive !== undefined ? rawData.isActive : true,
    assignedCount: rawData.assignedCount || rawData.tableCount || 0,
    lastLogin: rawData.lastLogin || rawData.lastLoginAt || null,
    createdAt: rawData.createdAt || rawData.created || undefined,
    activeRequests: rawData.activeRequests || rawData.pendingRequests || 0,
    financeOnlyRestricted: Boolean(rawData.financeOnlyRestricted),
  };
}

/**
 * Format last login timestamp into human-readable format
 * Returns relative time like "2 hours ago" or "Never" if no login
 */
export function formatLastLogin(lastLogin?: string | null): string {
  if (!lastLogin) {
    return 'Never';
  }

  try {
    const loginDate = new Date(lastLogin);
    const now = new Date();
    const diffMs = now.getTime() - loginDate.getTime();

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    console.error('Error formatting last login:', error);
    return 'Unknown';
  }
}

/**
 * Compute aggregate statistics from array of stewards
 * Used for dashboard metrics/cards
 */
export function computeStewardStats(stewards: Steward[]): StewardStats {
  const stats: StewardStats = {
    total: stewards.length,
    active: 0,
    inactive: 0,
    totalAssigned: 0,
    totalActive: 0,
  };

  stewards.forEach((steward) => {
    if (steward.active) {
      stats.active++;
      stats.totalActive += steward.activeRequests || 0;
    } else {
      stats.inactive++;
    }
    stats.totalAssigned += steward.assignedCount || 0;
  });

  return stats;
}
